import { CATALOG_2026_MODULES } from "./catalog2026";
import { TENANT_MODULES } from "./tenantModules";

// Die 4 eingebauten IDs sind der Default-Plan; Tenants können zusätzlich bis zu
// 2 eigene Pläne anlegen und alle Namen umbenennen (siehe utils/trainingPlans.ts).
// Deshalb bleibt der Typ ein offener string statt einer festen Union.
export type TrainingLevel = string;
export const DEFAULT_TRAINING_LEVEL_IDS = ["basis", "standard", "erweitert", "compliance"] as const;

export const TRAINING_LEVEL_LABELS: Record<TrainingLevel, string> = {
  basis: "Basis",
  standard: "Standard",
  erweitert: "Erweitert",
  compliance: "Compliance & Industrie",
};

// Das Level eines Moduls steuert, ab welchem Trainingsplan es zugewiesen wird
// (kumulativ: Basis ⊂ Standard ⊂ Erweitert ⊂ Compliance). Welche Module ein
// Mandant überhaupt hat, entscheidet allein die Lizenz (siehe unten).
export type SecurityModuleMeta = {
  id: string;
  topicId: string;
  title: string;
  shortTitle: string;
  description: string;
  duration: string;
  color: string;
  icon: string;
  focusAreas: string[];
  scenario: string;
  level: TrainingLevel;
};

export const DETAILED_MODULE_IDS = [
  "general-safety-briefing",
] as const;

// Module außerhalb des Katalogs 2026. Was ein Mandant tatsächlich sieht, steht
// in SECURITY_MODULES – dort landen ausschließlich die im GA-Portal
// lizenzierten Module (siehe applyLicensedModules weiter unten).
const BASE_SECURITY_MODULES: SecurityModuleMeta[] = [
  {
    id: "general-safety-briefing",
    topicId: "general-safety-briefing",
    title: "Allgemeine Sicherheitsunterweisung",
    shortTitle: "Sicherheitsunterweisung",
    description: "Die jährliche Pflichtunterweisung: sicher arbeiten, Gefahrstoffe, PSA und Maschinen – dazu die Notfallkette von Absichern über Notruf und Erste Hilfe bis Brandschutz und Evakuierung.",
    duration: "15 Min.",
    color: "#0d9488",
    icon: "🦺",
    focusAreas: ["Unterweisungspflicht & Meldewege", "Beinaheunfälle melden", "Gefahrstoffe & Kennzeichnung", "PSA richtig nutzen", "Maschinensicherheit", "Erste Hilfe & Notruf", "Brandschutz & Evakuierung"],
    scenario: "Aus einer Maschine tritt Öl aus und hinterlässt einen rutschigen Film auf dem Hallenboden. Gestürzt ist noch niemand, Ihre Schicht endet in zehn Minuten.",
    level: "basis",
  },
];

/**
 * Für den Mandanten sichtbare Module: genau die im GA-Portal lizenzierten.
 * Startet leer und wird von EntitlementContext gefüllt (applyLicensedModules) –
 * bewusst dasselbe veränderliche Array wie bisher, weil sehr viele Stellen
 * direkt daraus lesen (siehe applyPlatformModuleMeta).
 */
export const SECURITY_MODULES: SecurityModuleMeta[] = [];

/**
 * Alle ausgelieferten Module ohne Lizenzfilter – der komplette Katalog plus die
 * Module außerhalb davon. Grundlage fürs GA-Portal (Modul-Editor und
 * Freischaltung pro Mandant) und für alle nach ID gekeyten Nachschlagetabellen.
 */
export const ALL_PLATFORM_MODULES: SecurityModuleMeta[] = [...BASE_SECURITY_MODULES, ...CATALOG_2026_MODULES, ...TENANT_MODULES];

/** Anzahl der aktuell sichtbaren Module – Nenner für Abschlussquoten. */
export function totalSecurityModules(): number {
  return SECURITY_MODULES.length;
}
export const MIN_TRAINING_COMPLETION_SCORE = 7;

/** Returns all modules a user at the given level should complete (cumulative). */
export function getModulesForLevel(level: TrainingLevel): SecurityModuleMeta[] {
  if (level === "basis") return SECURITY_MODULES.filter(m => m.level === "basis");
  if (level === "standard") return SECURITY_MODULES.filter(m => m.level === "basis" || m.level === "standard");
  if (level === "erweitert") return SECURITY_MODULES.filter(m => m.level !== "compliance");
  return [...SECURITY_MODULES]; // compliance = alles (Erweitert + Industrie-Pflichten)
}

// Nachschlagetabellen bewusst über ALL_PLATFORM_MODULES: SECURITY_MODULES ist
// beim Import noch leer (die Lizenz kommt erst mit dem Entitlement) und eine
// einmal gebaute Tabelle würde die Lizenzänderung nicht mitbekommen.
export const MODULE_TO_TOPIC: Record<string, string> = ALL_PLATFORM_MODULES.reduce<Record<string, string>>((acc, module) => {
  acc[module.id] = module.topicId;
  return acc;
}, {});

export const TOPIC_LABELS_BY_ID: Record<string, string> = ALL_PLATFORM_MODULES.reduce<Record<string, string>>((acc, module) => {
  acc[module.topicId] = `${module.shortTitle}-Quiz`;
  return acc;
}, {});

const TOPIC_ALIASES: Record<string, string> = ALL_PLATFORM_MODULES.reduce<Record<string, string>>((acc, module) => {
  acc[module.topicId.toLowerCase()] = module.topicId;
  acc[module.id.toLowerCase()] = module.topicId;
  return acc;
}, {});

export function normalizeTrainingTopicId(value: string): string | null {
  const normalized = value.trim().toLowerCase();
  if (!normalized) return null;
  return TOPIC_ALIASES[normalized] ?? null;
}

// ── Englische Meta-Ebene ──────────────────────────────────────────────────
// DE (oben) bleibt kanonisch und wird im Admin-Bereich verwendet. Für den
// Mitarbeiterbereich liefert localizeModule() bei Sprache=EN die Übersetzung.
type ModuleMetaText = Pick<SecurityModuleMeta, "title" | "shortTitle" | "description" | "focusAreas" | "scenario">;

export const SECURITY_MODULE_TEXT_EN: Record<string, ModuleMetaText> = {
  "sec-01a-phishing-erkennen": {
    title: "Recognise and verify phishing",
    shortTitle: "Recognise phishing",
    description: "Professional design proves nothing. Check the sender, context and destination, then verify independently.",
    focusAreas: [
      "Recognise phishing regardless of appearance",
      "Check sender addresses and link destinations",
      "Assess urgency and unusual requests",
      "Report suspicious messages safely",
    ],
    scenario: "A supposed delivery notice uses a QR code to request an address update.",
  },
  "sec-01b-bec-anhaenge-und-meldeweg": {
    title: "BEC, attachments and secure payment approval",
    shortTitle: "BEC and payment approval",
    description: "A credible request for a small process exception can be enough to cause fraud. Verify changes independently.",
    focusAreas: [
      "Recognise BEC patterns and account takeovers",
      "Confirm payment and master data changes safely",
      "Assess attachment type and origin",
      "Respond quickly and preserve evidence after a mistake",
    ],
    scenario: "A familiar supplier sends a plausible notice changing bank details just before payment.",
  },
  "sec-02a-passwoerter-und-passwortmanager": {
    title: "Passwords and password managers",
    shortTitle: "Passwords and managers",
    description: "Use long, unique passwords in the approved manager and respond promptly if credentials are exposed.",
    focusAreas: [
      "Use strong, unique credentials",
      "Use the approved password manager correctly",
      "Avoid sharing and unsafe storage",
      "Secure compromised accounts and report promptly",
    ],
    scenario: "A team needs access to a customer portal and considers sharing one login.",
  },
  "sec-02b-mfa-und-kontowiederherstellung": {
    title: "MFA, push fatigue and account recovery",
    shortTitle: "MFA and account recovery",
    description: "Approve only sign-ins you initiated, recognise repeated push attacks and use controlled recovery.",
    focusAreas: [
      "Assess MFA requests safely",
      "Recognise push fatigue and one-time-code phishing",
      "Use controlled account recovery and device replacement",
      "Report suspected MFA incidents promptly",
    ],
    scenario: "Repeated MFA requests appear even though you are not signing in.",
  },
  "sec-03a-social-engineering-telefon-und-chat": {
    "title": "Social engineering by phone and chat",
    "shortTitle": "Phone and chat impersonation",
    "description": "Helpfulness becomes risky when identity and authorisation are not independently verified.",
    "focusAreas": [
      "Recognise psychological manipulation",
      "Verify identity independently",
      "Control sensitive information and actions",
      "Report suspicious contacts"
    ],
    "scenario": "A caller claiming to be the Service Desk knows your project and asks for an MFA code."
  },
  "sec-03b-physical-security-und-usb": {
    title: "Physical security, tailgating and USB",
    shortTitle: "Physical security and USB",
    description: "Check access, protect workspaces and handle found media safely.",
    focusAreas: ["Recognise unauthorised access", "Handle found media without connecting it", "Protect screens, documents and devices", "Report physical incidents safely"],
    scenario: "A person in company clothing carries boxes at a side door and asks you to hold it open.",
  },
  "sec-04a-homeoffice-und-mobile-arbeit": {
    "title": "Secure home working and mobile work",
    "shortTitle": "Home and mobile work",
    "description": "Protect conversations, devices, connections and data wherever you work.",
    "focusAreas": [
      "Assess the work setting before starting",
      "Protect screens, conversations and devices",
      "Use approved networks and storage",
      "Report loss or exposure promptly"
    ],
    "scenario": "A confidential video call starts on a crowded train."
  },
  "sec-04b-cloud-freigaben-und-datentransfer": {
    title: "Cloud sharing and secure data transfer",
    shortTitle: "Cloud sharing",
    description: "Classify data, limit cloud access, and respond promptly to accidental shares.",
    focusAreas: ["Classify data before sharing", "Choose recipients and minimum permissions", "Limit access by purpose and time", "Revoke and report accidental shares"],
    scenario: "A supplier needs selected project files for three weeks.",
  },
  "sec-05a-ki-daten-und-freigegebene-tools": {
    title: "AI tools: protect data and use approved services",
    shortTitle: "Safe AI tool use",
    description: "Check the tool and data before every prompt; minimise inputs and report accidental uploads.",
    focusAreas: ["Treat AI inputs as data transfers", "Use approved tools and accounts", "Remove or avoid sensitive data", "Report unsafe use or leakage"],
    scenario: "A customer meeting record needs a quick AI summary.",
  },
  "sec-05b-ki-ergebnisse-und-menschliche-kontrolle": {
    title: "Check AI outputs and use them responsibly",
    shortTitle: "Check AI outputs",
    description: "Verify AI claims and sources, match review to possible harm, and keep a named human decision owner.",
    focusAreas: ["Verify facts and sources", "Review according to risk", "Keep human accountability", "Document important use"],
    scenario: "An AI answer cites an apparently convincing legal provision for a customer response.",
  },
  "sec-06-informationssicherheit-im-arbeitsalltag": {
    title: "Information security in everyday work",
    shortTitle: "Information security",
    description: "Classify and protect information, use approved channels, verify requests and report observations early.",
    focusAreas: ["Apply protection requirements", "Secure identities and devices", "Use approved channels", "Report observations early"],
    scenario: "A colleague quietly deletes a phishing message after entering credentials on a fake site.",
  },
  "saf-01a-gefahren-erkennen-und-stoppen": {
    title: "Recognise hazards and stop work safely",
    shortTitle: "Hazards and stop-work",
    description: "Spot weak signals, reassess changes, stop unsafe work and report hazards before someone is hurt.",
    focusAreas: ["Recognise hazards", "Reassess changes", "Stop unsafe work", "Secure and report hazards"],
    scenario: "A leak makes the main walkway slippery. You can safely reach a barrier and warning sign.",
  },
  "saf-01b-verantwortung-und-sichere-kommunikation": {
    title: "Responsibility, instruction and clear safety communication",
    shortTitle: "Safe handovers and communication",
    description: "Know your role, confirm critical instructions and coordinate safe handovers with colleagues and contractors.",
    focusAreas: ["Know your role and limits", "Confirm critical information", "Coordinate handovers", "Brief contractors"],
    scenario: "A shift ends while a fault is only partly resolved and another team is still working near the equipment.",
  },
  "saf-02-gesundheit-ergonomie-und-belastung": {
    title: "Health, ergonomics and workload",
    shortTitle: "Health and ergonomics",
    description: "Recognise strain early, adjust work ergonomically and address fatigue through local support routes.",
    focusAreas: ["Recognise early strain", "Adjust work ergonomically", "Address fatigue", "Use support routes"],
    scenario: "Shoulder and hand pain appears after several shifts of repetitive assembly.",
  },
  "saf-03a-psa-auswahl-und-anwendung": {
    title: "Use personal protective equipment correctly",
    shortTitle: "Select and use PPE",
    description: "Select, check and use PPE correctly; stop work and replace equipment that is missing or defective.",
    focusAreas: ["Controls before PPE", "Select and check PPE", "Check fit and compatibility", "Remove defective PPE from service"],
    scenario: "Your safety glasses fog during a hazardous task, or a helmet buckle is broken.",
  },
  "saf-03b-gefahrstoffe-sicher-handhaben": {
    title: "Recognize and handle hazardous substances safely",
    shortTitle: "Handle hazardous substances",
    description: "Read labels, safety data and workplace instructions; prevent exposure and respond safely to leaks.",
    focusAreas: ["Read hazard information", "Prevent exposure", "Use suitable containers", "Respond to leaks and exposure"],
    scenario: "You find an unlabelled spray bottle or a leaking container in storage.",
  },
  "saf-04a-brandschutz-und-evakuierung": {
    title: "Fire safety and safe evacuation",
    shortTitle: "Fire safety and evacuation",
    description: "Prevent fire hazards, raise the alarm, use safe escape routes and confirm your presence at the assembly point.",
    focusAreas: ["Prevent fire hazards", "Raise the alarm", "Use safe escape routes", "Report at the assembly point"],
    scenario: "Smoke blocks the main corridor after the fire alarm.",
  },
  "saf-04b-erste-hilfe-und-notfallkommunikation": {
    title: "First aid and emergency communication",
    shortTitle: "First aid and emergencies",
    description: "Protect yourself, raise the alarm early, involve trained first aiders and hand over clear facts.",
    focusAreas: ["Assess safety", "Call for help early", "Involve first aiders and AED", "Hand over clear information"],
    scenario: "An unresponsive person is found at work.",
  },
  "saf-05a-werkstatt-und-handwerkzeuge": {
    title: "Workshop safety and hand tools",
    shortTitle: "Workshop tools",
    description: "Choose suitable tools, inspect them before use, secure workpieces and remove defective equipment from service.",
    focusAreas: ["Select suitable tools", "Inspect before use", "Secure workpieces", "Mark and report defects"],
    scenario: "An extension cable has crushed insulation, but the tool still runs.",
  },
  "saf-05b-maschinen-und-energieisolierung": {
    title: "Machine safety and energy isolation",
    shortTitle: "Machine safety",
    description: "A stopped machine may still contain hazardous energy. Check guards, follow the approved isolation procedure and coordinate restart.",
    focusAreas: ["Assess machine guards", "Handle faults safely", "Isolate and verify energy", "Coordinate restart"],
    scenario: "A workpiece jams inside a guarded machine while two teams are present.",
  },
  "com-01-code-of-conduct": {
    title: "Code of Conduct",
    shortTitle: "Code of Conduct",
    description: "Make transparent decisions, keep accurate records, respect people and raise concerns through the right channels.",
    focusAreas: ["Use the Code as a decision guide", "Apply the four-question integrity check", "Disclose conflicts and gifts", "Report concerns without investigating yourself"],
    scenario: "A buyer must evaluate a supplier connected to a close family member.",
  },
  "general-safety-briefing": {
    title: "General Safety Briefing",
    shortTitle: "Safety Briefing",
    description: "The mandatory annual briefing: working safely, hazardous substances, PPE and machine safety – plus the emergency chain from making the scene safe through the emergency call and first aid to fire safety and evacuation.",
    focusAreas: ["Briefing duty & reporting channels", "Report near misses", "Hazardous substances & labelling", "Using PPE correctly", "Machine safety", "First aid & emergency call", "Fire safety & evacuation"],
    scenario: "Oil is leaking from a machine and leaves a slippery film on the shop floor. Nobody has slipped yet and your shift ends in ten minutes.",
  },
};

export const TRAINING_LEVEL_LABELS_EN: Record<TrainingLevel, string> = {
  basis: "Basic",
  standard: "Standard",
  erweitert: "Advanced",
  compliance: "Compliance & Industry",
};

export function trainingLevelLabel(level: TrainingLevel, lang: "de" | "en"): string {
  // Tenant-eigene Pläne haben hier kein Label – dann die Plan-ID zeigen statt undefined.
  return (lang === "en" ? TRAINING_LEVEL_LABELS_EN : TRAINING_LEVEL_LABELS)[level] ?? level;
}

// ── Globale Modul-Overrides aus dem GA-Portal ─────────────────────────────
// Die Metadaten der Code-Module werden an sehr vielen Stellen direkt aus
// SECURITY_MODULES gelesen. Statt jede dieser Stellen umzubauen, spielt
// EntitlementContext die im GA-Portal gepflegten Werte einmal pro Session
// hier ein. `CODE_META_BASELINE` hält den Auslieferungsstand fest, damit ein
// erneutes Anwenden (oder ein zurückgenommener Override) nicht auf bereits
// überschriebenen Werten aufsetzt.
export type PlatformMetaText = Partial<Pick<SecurityModuleMeta, "title" | "shortTitle" | "description" | "duration" | "scenario" | "focusAreas">>;
export type PlatformModuleMeta = {
  icon?: string;
  color?: string;
  level?: TrainingLevel;
  /** Videos des Moduls müssen vollständig angesehen werden. */
  forceVideo?: boolean;
  de?: PlatformMetaText;
  en?: PlatformMetaText;
};

// Nach Modul-ID statt nach Position, weil sich die Länge von SECURITY_MODULES
// mit den lizenzierten Katalogmodulen ändert.
const CODE_META_BASELINE: Record<string, SecurityModuleMeta> = Object.fromEntries(
  ALL_PLATFORM_MODULES.map(m => [m.id, { ...m, focusAreas: [...m.focusAreas] }]),
);
const CODE_TEXT_EN_BASELINE: Record<string, ModuleMetaText> = JSON.parse(JSON.stringify(SECURITY_MODULE_TEXT_EN)) as Record<string, ModuleMetaText>;

/**
 * Auslieferungsstand der Metadaten eines Moduls – ohne GA- und Mandanten-
 * Overrides. SECURITY_MODULES ist im Adminbereich bereits überschrieben; wer
 * „die SHERLOX-Fassung" braucht (Modul-Editor, Update-Abgleich), liest hier.
 */
export function codeModuleMeta(id: string): {
  icon: string;
  color: string;
  level: string;
  de: Required<PlatformMetaText>;
  en: Required<PlatformMetaText>;
} | null {
  const base = CODE_META_BASELINE[id];
  if (!base) return null;
  const de = {
    title: base.title,
    shortTitle: base.shortTitle,
    description: base.description,
    duration: base.duration,
    scenario: base.scenario,
    focusAreas: [...base.focusAreas],
  };
  const enText = CODE_TEXT_EN_BASELINE[id];
  const en = enText ? { ...de, ...enText, focusAreas: [...enText.focusAreas] } : { ...de, focusAreas: [...de.focusAreas] };
  return { icon: base.icon, color: base.color, level: base.level, de, en };
}

/** Zuletzt eingespielte GA-Overrides – nach einer Lizenzänderung erneut anzuwenden. */
let lastPlatformOverrides: Record<string, PlatformModuleMeta> = {};

export function applyPlatformModuleMeta(overrides: Record<string, PlatformModuleMeta>): void {
  lastPlatformOverrides = overrides;
  SECURITY_MODULES.forEach((module) => {
    const baseline = CODE_META_BASELINE[module.id];
    if (!baseline) return;
    Object.assign(module, { ...baseline, focusAreas: [...baseline.focusAreas] });
    const override = overrides[module.id];
    if (!override) return;
    if (override.icon) module.icon = override.icon;
    if (override.color) module.color = override.color;
    if (override.level) module.level = override.level;
    if (override.de) Object.assign(module, override.de);
  });

  for (const id of Object.keys(SECURITY_MODULE_TEXT_EN)) {
    if (!CODE_TEXT_EN_BASELINE[id]) delete SECURITY_MODULE_TEXT_EN[id];
  }
  for (const [id, text] of Object.entries(CODE_TEXT_EN_BASELINE)) {
    SECURITY_MODULE_TEXT_EN[id] = { ...text, focusAreas: [...text.focusAreas] };
  }
  for (const [id, override] of Object.entries(overrides)) {
    if (!override.en) continue;
    // Modul ohne EN-Eintrag im Code: deutsche Basis nehmen, damit keine
    // halbleere Übersetzung entsteht, und den Override darüberlegen.
    const module = SECURITY_MODULES.find(m => m.id === id);
    const base: ModuleMetaText = SECURITY_MODULE_TEXT_EN[id] ?? (module
      ? { title: module.title, shortTitle: module.shortTitle, description: module.description, focusAreas: [...module.focusAreas], scenario: module.scenario }
      : { title: "", shortTitle: "", description: "", focusAreas: [], scenario: "" });
    SECURITY_MODULE_TEXT_EN[id] = { ...base, ...override.en };
  }
}

// ── Lizenzierte Module ────────────────────────────────────────────────────
// Alle Module stehen im Code, sind aber für keinen Mandanten sichtbar, bis sie
// im GA-Portal freigeschaltet wurden (features.licensed_modules). Statt jede
// Lesestelle um einen Lizenzfilter zu erweitern, wird SECURITY_MODULES selbst
// auf den lizenzierten Umfang gebracht — was hier nicht drinsteht, existiert
// für den Mitarbeiter-, Manager- und Admin-Bereich schlicht nicht.

/**
 * Bringt SECURITY_MODULES auf die im GA-Portal lizenzierten Module.
 * Gibt `true` zurück, wenn sich die sichtbare Modulmenge geändert hat.
 *
 * Bewusst **nicht** sprachabhängig: ein Modul, das je nach Oberflächensprache
 * verschwindet, würde Abschlüsse, Erinnerungsmails und Themen-Zertifikate
 * auseinanderlaufen lassen. Fehlt die englische Fassung (CATALOG_2026_EN_READY),
 * weist das Modul im englischen Modus darauf hin, statt zu verschwinden.
 */
export function applyLicensedModules(licensedIds: readonly string[]): boolean {
  const licensed = new Set(licensedIds);
  const next = ALL_PLATFORM_MODULES.filter(m => licensed.has(m.id));
  const nextIds = next.map(m => m.id);
  const currentIds = SECURITY_MODULES.map(m => m.id);
  if (nextIds.length === currentIds.length && nextIds.every((id, i) => id === currentIds[i])) return false;

  const keep = new Map(SECURITY_MODULES.map(m => [m.id, m]));
  SECURITY_MODULES.splice(0, SECURITY_MODULES.length, ...next.map(m => (
    // Bereits eingespielte GA-Overrides nicht verlieren: bekannte Objekte behalten.
    keep.get(m.id) ?? { ...m, focusAreas: [...m.focusAreas] }
  )));
  applyPlatformModuleMeta(lastPlatformOverrides);
  return true;
}

/** Liefert das Modul-Meta in der gewünschten Sprache (DE = unverändert, EN = übersetzt). */
export function localizeModule(meta: SecurityModuleMeta, lang: "de" | "en"): SecurityModuleMeta {
  if (lang !== "en") return meta;
  const en = SECURITY_MODULE_TEXT_EN[meta.id];
  return en ? { ...meta, ...en } : meta;
}

// ── Echte Video-Laufzeiten ────────────────────────────────────────────────
// Gerundete Minuten – referenziert im LearningModule (Video-Schritt +
// Mindest-Schauzeit-Gate) und in der Trainingsbereich-Übersicht. Module ohne
// Eintrag fallen auf die Modul-Gesamtschätzung zurück.
export const MODULE_VIDEO_DURATION: Record<string, { de: string; en: string }> = {
  "saf-01a-gefahren-erkennen-und-stoppen": { de: "4 Min.", en: "4 min" },
  "saf-01b-verantwortung-und-sichere-kommunikation": { de: "4 Min.", en: "4 min" },
  "saf-02-gesundheit-ergonomie-und-belastung": { de: "4 Min.", en: "4 min" },
  "saf-03a-psa-auswahl-und-anwendung": { de: "4 Min.", en: "4 min" },
  "saf-03b-gefahrstoffe-sicher-handhaben": { de: "4 Min.", en: "4 min" },
  "saf-04a-brandschutz-und-evakuierung": { de: "4 Min.", en: "4 min" },
  "saf-04b-erste-hilfe-und-notfallkommunikation": { de: "4 Min.", en: "4 min" },
  "saf-05a-werkstatt-und-handwerkzeuge": { de: "4 Min.", en: "4 min" },
  "saf-05b-maschinen-und-energieisolierung": { de: "4 Min.", en: "4 min" },
  "com-01-code-of-conduct": { de: "5 Min.", en: "5 min" },
  // Gesamtfassung vom 22.08.2026 (12:21) – ersetzt den früheren Zweiteiler.
  "general-safety-briefing": { de: "12 Min.", en: "12 min" },
  "cpt-01-strategie-2030": { de: "17 Min.", en: "17 min" },
  // Fassung vom 10.09.2026 (13:20) – gestraffter Einstieg.
  "cpt-02-informationssicherheit": { de: "13 Min.", en: "13 min" },
};

/** Anzuzeigende Video-Laufzeit: echte Laufzeit, sonst Modul-Gesamtschätzung. */
export function moduleVideoDurationLabel(meta: SecurityModuleMeta, lang: "de" | "en"): string {
  const real = MODULE_VIDEO_DURATION[meta.id]?.[lang];
  if (real) return real;
  return lang === "en" ? meta.duration.replace("Min.", "min") : meta.duration;
}

// ── Fun Facts ("Spannend zu wissen") ──────────────────────────────────────
// Ein einprägsamer Fakt pro Modul (DE+EN), wird als Callout-Block eingeblendet.
export const MODULE_FUN_FACTS: Record<string, { de: string; en: string }> = {
  "cpt-01-strategie-2030": {
    de: "„Strategie“ kommt vom griechischen strategós, dem Heerführer – gemeint war nie der Marschplan, sondern die Entscheidung, wo überhaupt gekämpft wird. Genau daran hat sich nichts geändert: Die schwierigste strategische Frage ist nicht, wie man etwas macht, sondern was man bewusst nicht tut.",
    en: "The word 'strategy' comes from the Greek strategós, the army commander – and it never meant the marching plan, but the decision about where to fight at all. Nothing has changed there: the hardest strategic question is not how to do something, but what to deliberately leave undone.",
  },
  "cpt-02-informationssicherheit": {
    de: "Schon 1883 hat der Kryptograf Auguste Kerckhoffs festgehalten, dass ein Verfahren sicher bleiben muss, selbst wenn alle es kennen – geheim gehört nur der Schlüssel. Genau deshalb schützen uns Verschlüsselung, MFA und feste Übertragungswege besser als die Hoffnung, dass niemand weiß, wo etwas liegt.",
    en: "As early as 1883, the cryptographer Auguste Kerckhoffs noted that a system must stay secure even if everyone knows how it works – only the key is secret. That is exactly why encryption, MFA and fixed transfer routes protect us better than hoping nobody knows where something is stored.",
  },
  "general-safety-briefing": {
    de: "Auf jeden schweren Arbeitsunfall kommen statistisch hunderte Beinaheunfälle, die niemand meldet (Unfallpyramide nach Heinrich). Die Meldung, die dir unwichtig vorkommt, ist genau die Information, die den nächsten Unfall verhindert.",
    en: "Statistically, every serious accident at work is preceded by hundreds of near misses that nobody reports (Heinrich's accident triangle). The report that feels unimportant is exactly the information that prevents the next accident.",
  },
};
